import { useEffect, useRef, useState } from 'react';
import { authenticatedRequest } from '../services/authApi';
import { GEO, GEO_ALIAS_PRESETS } from '../components/natal/natalConstants';

export const NATAL_GEO_RUNTIME_VERSION = 'f2y-browser-trace-v1';

function debugGeoTrace(marker, rows = [], writer = null) {
  if (process.env.NODE_ENV !== 'production') {
    const safeRows = Array.isArray(rows)
      ? rows.map((row, index) => geoRuntimeRowMetadata(row, index))
      : [];
    console.debug(marker, {
      runtimeVersion: NATAL_GEO_RUNTIME_VERSION,
      ...(writer ? { writer } : {}),
      count: safeRows.length,
      rows: safeRows,
    });
  }
}

function debugGeoStateCommit(writer, inputCount, output) {
  if (process.env.NODE_ENV !== 'production') {
    debugGeoTrace('[NATAL_GEO_COMMIT_TRACE]', output, writer);
  }
}

function debugGeoRequest(requestId, writer, q, country, province, resultCount = 0) {
  if (process.env.NODE_ENV !== 'production') {
    console.debug('[NATAL_GEO_REQUEST_TRACE]', {
      runtimeVersion: NATAL_GEO_RUNTIME_VERSION,
      requestId,
      writer,
      q: String(q || '').slice(0, 160),
      country: String(country || '').slice(0, 16),
      province: String(province || '').slice(0, 64),
      resultCount: Math.max(0, Number(resultCount) || 0),
    });
  }
}

function debugGeoResponseCommit(requestId, writer, inputCount, output) {
  if (process.env.NODE_ENV !== 'production') {
    console.debug('[NATAL_GEO_RESPONSE_COMMIT]', {
      runtimeVersion: NATAL_GEO_RUNTIME_VERSION,
      requestId,
      writer,
      inputCount: Math.max(0, Number(inputCount) || 0),
      outputCount: Array.isArray(output) ? output.length : 0,
      names: Array.isArray(output)
        ? output.map((row) => String(row?.name || row?.primaryName || '').slice(0, 96))
        : [],
    });
  }
}

function toSearchParams(values) {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null) params.set(key, String(value));
  });
  return params;
}

export function geoRequestProvince(province = '') {
  return province === '__ANY__' ? '' : String(province || '');
}

export function geoRequestQuery(query = '', province = '') {
  const typed = String(query || '').trim();
  const selectedProvince = String(province || '').trim();
  if (!typed || selectedProvince === 'CABA' || selectedProvince === '__ANY__') return typed;
  return typed;
}

function normalizeGeoAlias(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s,()-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizeGeoText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const LOCALITY_TYPES = new Set([
  'city', 'town', 'village', 'municipality', 'borough', 'suburb',
  'neighbourhood', 'neighborhood', 'quarter', 'locality', 'hamlet',
  'administrative', 'district', 'county', 'commune',
]);

const MEDIUM_TYPES = new Set([
  'island', 'territory', 'state', 'province', 'administrative_area_level_1',
]);

const GEO_TYPE_PRIORITY = Object.freeze({
  city: 100,
  town: 95,
  village: 90,
  municipality: 85,
  borough: 80,
  suburb: 75,
  neighbourhood: 75,
  neighborhood: 75,
  quarter: 70,
  locality: 70,
  hamlet: 65,
  administrative: 45,
  district: 40,
  commune: 40,
  station: 15,
  railway: 12,
  road: 8,
  street: 8,
  amenity: 5,
  building: 4,
  poi: 3,
});

const POI_TYPES = new Set([
  'road', 'street', 'house', 'building', 'amenity', 'shop', 'tourism',
  'railway', 'station', 'church', 'school', 'hospital', 'restaurant',
  'business', 'office', 'address', 'poi', 'place_of_worship',
]);

const EXPLICIT_POI_QUERY_TERMS = new Set([
  'estacion', 'station', 'railway', 'terminal', 'airport', 'aeropuerto',
]);

function geoType(pick = {}) {
  return normalizeGeoText(
    pick.resultType || pick.result_type || pick.type || pick.addresstype || pick.class
  );
}

function geoAddressValues(pick = {}, query = '') {
  const address = pick.address && typeof pick.address === 'object' ? pick.address : {};
  const normalizedQuery = normalizeGeoText(query);
  const namedLocality = String(pick.primaryName || pick.name || '').trim();
  const exactNamedLocality = normalizedQuery && normalizeGeoText(namedLocality) === normalizedQuery;
  const preferNamedLocality = namedLocality && (exactNamedLocality || !POI_TYPES.has(geoType(pick)));
  return {
    locality: [
      ...(preferNamedLocality ? [namedLocality] : []),
      pick.primaryName,
      pick.locality,
      address.neighbourhood,
      address.neighborhood,
      address.suburb,
      address.quarter,
      address.city_district,
      address.borough,
      address.district,
      address.city,
      address.town,
      address.village,
      address.municipality,
      pick.city,
      pick.town,
      pick.village,
      pick.municipality,
      pick.suburb,
      pick.neighbourhood,
      pick.borough,
      ...(!preferNamedLocality ? [pick.name] : []),
    ].filter(Boolean),
    region: [
      pick.region,
      pick.admin1,
      pick.state,
      address.state,
      address.province,
      address.region,
      pick.province,
    ].filter(Boolean),
    country: [pick.country, address.country].filter(Boolean),
  };
}

function canonicalRegionName(value, pick = {}) {
  const normalized = normalizeGeoText(value);
  const country = normalizeGeoText(canonicalCountryName(firstDistinct(geoAddressValues(pick).country), pick));
  const contextLooksCaba = normalizeGeoText(pick.province) === 'caba' ||
    normalizeGeoText(pick.province).includes('capital federal') ||
    normalizeGeoText(pick.address?.city_district).startsWith('comuna ') ||
    normalizeGeoText(pick.address?.city).includes('ciudad autonoma de buenos aires');

  if (country === 'argentina' && (contextLooksCaba || normalized === 'capital federal' || normalized === 'caba')) {
    if (normalized === 'buenos aires' || normalized === 'capital federal' || normalized === 'caba') {
      return 'Ciudad Autónoma de Buenos Aires';
    }
  }

  return String(value || '').trim();
}

const COUNTRY_DISPLAY_NAMES = Object.freeze({
  AR: 'Argentina', IT: 'Italia', US: 'United States', GB: 'United Kingdom',
  FR: 'France', ES: 'España', DE: 'Germany', NO: 'Norway', CH: 'Switzerland',
  AU: 'Australia', NZ: 'New Zealand', BR: 'Brasil',
});

function canonicalCountryName(value, pick = {}) {
  const normalized = normalizeGeoText(value);
  const code = String(pick.countryCode || (String(value || '').trim().length === 2 ? value : '')).trim().toUpperCase();
  return COUNTRY_DISPLAY_NAMES[code] || (normalized.length === 2 ? String(value || '').toUpperCase() : String(value || '').trim());
}

const COUNTRY_CODES_BY_NAME = Object.freeze({
  argentina: 'AR', brasil: 'BR', brazil: 'BR', chile: 'CL', colombia: 'CO',
  guatemala: 'GT', italia: 'IT', italy: 'IT', mexico: 'MX', méxico: 'MX',
  paraguay: 'PY', peru: 'PE', perú: 'PE', spain: 'ES', españa: 'ES',
  uruguay: 'UY', 'united states': 'US', 'estados unidos': 'US',
});

function countryCodeForCandidate(candidate = {}) {
  const address = candidate.address && typeof candidate.address === 'object'
    ? candidate.address
    : {};
  const raw = candidate.countryCode || candidate.country_code || address.country_code || candidate.country || address.country || '';
  const normalized = normalizeGeoText(raw);
  return String(raw).trim().length === 2
    ? String(raw).trim().toUpperCase()
    : COUNTRY_CODES_BY_NAME[normalized] || '';
}

export function filterGeoCandidatesForContext(candidates = [], context = {}) {
  const expectedRaw = String(context.country || '').trim();
  const expected = expectedRaw.length === 2
    ? expectedRaw.toUpperCase()
    : COUNTRY_CODES_BY_NAME[normalizeGeoText(expectedRaw)] || '';
  if (!expected) return candidates;
  return candidates.filter((candidate) => {
    const actual = countryCodeForCandidate(candidate);
    return !actual || actual === expected;
  });
}

function firstDistinct(values) {
  const seen = new Set();
  return values.find((value) => {
    const normalized = normalizeGeoText(value);
    if (!normalized || seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  }) || '';
}

export function canonicalGeoLabel(pick = {}, context = {}, query = '') {
  const source = { ...context, ...pick };
  const addressValues = geoAddressValues(source, query);
  const primary = firstDistinct(addressValues.locality) || String(pick.name || '').trim();
  const region = canonicalRegionName(firstDistinct(addressValues.region), source);
  const country = canonicalCountryName(firstDistinct(addressValues.country), source);
  const seen = new Set();
  const parts = [primary, region, country].filter((value) => {
    const normalized = normalizeGeoText(value);
    if (!normalized || seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
  const fallback = String(pick.display || pick.complete_name || pick.name || '').trim();
  return parts.length >= 2 ? parts.join(', ') : fallback;
}

export function geoRuntimeRowMetadata(pick = {}, index = 0) {
  const addressValues = geoAddressValues(pick, pick.name || '');
  const locality = firstDistinct(addressValues.locality);
  const city = firstDistinct([
    pick.city,
    pick.town,
    pick.village,
    pick.address?.city,
    pick.address?.town,
    pick.address?.village,
  ]);
  const admin1 = canonicalRegionName(firstDistinct(addressValues.region), pick);
  const country = canonicalCountryName(firstDistinct(addressValues.country), pick);
  return {
    index,
    provider: String(pick.provider || pick.source || 'unknown').slice(0, 32),
    name: String(pick.name || pick.primaryName || '').slice(0, 96),
    canonicalLocality: String(locality || '').slice(0, 96),
    canonicalCity: String(city || '').slice(0, 96),
    canonicalAdmin1: String(admin1 || '').slice(0, 96),
    country: String(country || '').slice(0, 64),
    placeFamily: geoType(pick),
    humanType: humanGeoTypeLabel(pick),
    semanticIdentity: semanticGeoIdentity(pick, {}, pick.name || '').slice(0, 160),
    canonicalLabel: String(pick.canonicalName || canonicalGeoLabel(pick, {}, pick.name || '')).slice(0, 160),
  };
}

export function humanGeoTypeLabel(pick = {}) {
  const type = geoType(pick);
  if (['city', 'town', 'village', 'locality', 'hamlet'].includes(type)) return 'Ciudad / localidad';
  if (['suburb', 'neighbourhood', 'neighborhood', 'quarter', 'borough'].includes(type)) return 'Barrio';
  if (type === 'municipality') return 'Municipio';
  if (['administrative', 'district', 'commune', 'state', 'province'].includes(type)) {
    const address = pick.address && typeof pick.address === 'object' ? pick.address : {};
    if (address.suburb || address.neighbourhood || address.neighborhood || address.quarter) return 'Barrio';
    if (address.city || address.town || address.village || pick.city || pick.town || pick.village) return 'Ciudad / localidad';
    return 'Área administrativa';
  }
  if (['station', 'railway'].includes(type)) return 'Estación';
  if (['road', 'street'].includes(type)) return 'Calle';
  if (['amenity', 'shop', 'restaurant', 'business', 'office', 'poi'].includes(type)) return 'Lugar de interés';
  return '';
}

function contextMatches(pick, context = {}) {
  const haystack = normalizeGeoText([
    pick.display, pick.complete_name, pick.name, pick.primaryName,
    pick.city, pick.town, pick.village, pick.municipality,
    pick.admin1, pick.region, pick.state, pick.country,
    pick.address && Object.values(pick.address),
  ].flat().join(' '));
  return [context.country, context.province, context.city]
    .map(normalizeGeoText)
    .filter(Boolean)
    .reduce((score, value) => {
      if (value.length <= 2) {
        return score + (normalizeGeoText(pick.countryCode) === value ? 1 : 0);
      }
      return score + (haystack.includes(value) ? 1 : 0);
    }, 0);
}

export function rankGeoCandidate(pick = {}, query = '', context = {}) {
  const normalizedQuery = normalizeGeoText(query);
  const type = geoType(pick);
  const names = geoAddressValues(pick).locality.map(normalizeGeoText).filter(Boolean);
  const primary = names[0] || normalizeGeoText(pick.name);
  let score = 0;

  if (LOCALITY_TYPES.has(type)) score += 80;
  else if (MEDIUM_TYPES.has(type)) score += 28;
  if (POI_TYPES.has(type) || normalizeGeoText(pick.class) === 'amenity') score -= 80;

  if (primary && primary === normalizedQuery) score += 70;
  else if (primary && primary.startsWith(normalizedQuery)) score += 35;
  else if (primary && primary.includes(normalizedQuery)) score += 12;

  if (names.some((name) => name === normalizedQuery)) score += 25;
  score += contextMatches(pick, context) * 18;

  const importance = Number(pick.importance ?? pick._providerImportance);
  if (Number.isFinite(importance)) score += Math.max(0, Math.min(1, importance)) * 20;

  const display = normalizeGeoText(pick.display || pick.complete_name);
  if (display && normalizedQuery && display.startsWith(normalizedQuery)) score += 8;
  if (display && display.split(',').length >= 5) score -= 8;
  if (Number.isFinite(Number(pick.place_rank))) score += Math.max(0, 20 - Number(pick.place_rank));

  return score;
}

function geoIdentity(pick = {}) {
  const addressValues = geoAddressValues(pick);
  const locality = normalizeGeoText(firstDistinct(addressValues.locality));
  const region = normalizeGeoText(firstDistinct(addressValues.region));
  const country = normalizeGeoText(firstDistinct(addressValues.country));
  const type = geoType(pick);
  const lat = Number(pick.lat);
  const lon = Number(pick.lon);
  const coordinate = Number.isFinite(lat) && Number.isFinite(lon)
    ? `${lat.toFixed(3)}:${lon.toFixed(3)}`
    : '';
  const fallback = normalizeGeoText(pick.display || pick.complete_name || pick.name);
  const identity = `${locality}|${region}|${country}|${type}|${coordinate}`;
  return locality || region || country || coordinate
    ? identity
    : `${identity}|${fallback}`;
}

function semanticGeoIdentity(pick = {}, context = {}, query = '') {
  return normalizeGeoText(canonicalGeoLabel(pick, context, query));
}

function coordinatesNear(a = {}, b = {}) {
  const aLat = Number(a.lat);
  const aLon = Number(a.lon);
  const bLat = Number(b.lat);
  const bLon = Number(b.lon);
  if (![aLat, aLon, bLat, bLon].every(Number.isFinite)) return false;
  return Math.abs(aLat - bLat) <= 0.08 && Math.abs(aLon - bLon) <= 0.08;
}

export function hasExplicitGeoPoiIntent(query = '') {
  return normalizeGeoText(query)
    .split(' ')
    .some((token) => EXPLICIT_POI_QUERY_TERMS.has(token));
}

export function dedupeGeoCandidates(candidates = [], context = {}, query = '') {
  const exactSeen = new Set();
  const semanticGroups = new Map();
  const output = [];

  candidates.forEach((candidate) => {
    const exactIdentity = geoIdentity(candidate);
    if (exactSeen.has(exactIdentity)) return;
    exactSeen.add(exactIdentity);

    const semanticIdentity = semanticGeoIdentity(candidate, context, query);
    const existingIndex = semanticGroups.get(semanticIdentity);
    if (existingIndex == null) {
      semanticGroups.set(semanticIdentity, output.length);
      output.push(candidate);
      return;
    }

    const existing = output[existingIndex];
    if (!coordinatesNear(existing, candidate)) {
      output.push(candidate);
      return;
    }

    const existingType = geoType(existing);
    const candidateType = geoType(candidate);
    const existingPriority = GEO_TYPE_PRIORITY[existingType] || 0;
    const candidatePriority = GEO_TYPE_PRIORITY[candidateType] || 0;
    const existingScore = Number(existing._geoRank) || 0;
    const candidateScore = Number(candidate._geoRank) || 0;
    if (candidatePriority > existingPriority ||
      (candidatePriority === existingPriority && candidateScore > existingScore)) {
      output[existingIndex] = candidate;
    }
  });

  return output;
}

export function rankGeoCandidates(candidates = [], query = '', context = {}) {
  return dedupeGeoCandidates(candidates, context, query)
    .map((candidate, index) => ({
      ...candidate,
      canonicalName: canonicalGeoLabel(candidate, context, query),
      resultType: candidate.resultType || candidate.result_type || candidate.type || candidate.addresstype || '',
      _geoRank: rankGeoCandidate(candidate, query, context),
      _geoIndex: index,
    }))
    .sort((a, b) => {
      if (b._geoRank !== a._geoRank) return b._geoRank - a._geoRank;
      return a._geoIndex - b._geoIndex;
    })
    .map(({ _geoRank, _geoIndex, ...candidate }) => candidate);
}

export function finalizeGeoResults(candidates = [], query = '', context = {}) {
  if (hasExplicitGeoPoiIntent(query)) {
    const localityCandidates = dedupeGeoCandidates(
      candidates.filter((candidate) => !['station', 'railway'].includes(geoType(candidate))),
      context,
      query
    );
    const stationCandidates = dedupeGeoCandidates(
      candidates.filter((candidate) => ['station', 'railway'].includes(geoType(candidate))),
      context,
      query
    );
    const output = [...localityCandidates, ...stationCandidates];
    return output;
  }

  const contextCandidates = filterGeoCandidatesForContext(candidates, context);
  const deduped = dedupeGeoCandidates(contextCandidates, context, query);
  const normalizedQuery = normalizeGeoText(query);
  const canonicalLocality = deduped.find((candidate) => {
    if (!LOCALITY_TYPES.has(geoType(candidate))) return false;
    const candidateLocality = normalizeGeoText(firstDistinct(geoAddressValues(candidate, query).locality));
    return candidateLocality === normalizedQuery;
  });
  if (!canonicalLocality) {
    return deduped;
  }

  const canonicalLocalityName = normalizeGeoText(
    firstDistinct(geoAddressValues(canonicalLocality, query).locality)
  );

  const output = deduped.filter((candidate) => {
    if (!['station', 'railway'].includes(geoType(candidate))) return true;
    const candidateLocality = normalizeGeoText(firstDistinct(geoAddressValues(candidate, query).locality));
    if (!candidateLocality) return true;
    return !(candidateLocality === canonicalLocalityName && coordinatesNear(canonicalLocality, candidate));
  });
  return output;
}

export function exactCanonicalLocalityExists(candidates = [], query = '', context = {}) {
  const normalizedQuery = normalizeGeoText(query);
  if (!normalizedQuery) return false;
  return filterGeoCandidatesForContext(candidates, context).some((candidate) => (
    LOCALITY_TYPES.has(geoType(candidate)) &&
    normalizeGeoText(firstDistinct(geoAddressValues(candidate, query).locality)) === normalizedQuery
  ));
}

export function canonicalGeoFields(pick = {}, previous = {}) {
  const lat = Number(pick.lat);
  const lon = Number(pick.lon);
  const canonicalName = pick.canonicalName || canonicalGeoLabel(pick, {}, pick.name || '');
  return {
    birthPlace: canonicalName || previous.birthPlace || '',
    birthLat: Number.isFinite(lat) ? lat : null,
    birthLon: Number.isFinite(lon) ? lon : null,
    birthTzOffsetMin: typeof pick.tzOffsetMin === 'number' ? pick.tzOffsetMin : null,
    birthTimezone: typeof pick.timezone === 'string' && pick.timezone ? pick.timezone : null,
  };
}

export function clearedGeoTemporalFields() {
  return { birthLat: null, birthLon: null, birthTzOffsetMin: null, birthTimezone: null };
}

export function isLatestGeoRequest(sequence, currentSequence) {
  return sequence === currentSequence;
}

export function findGeoAliasPreset({ country, province, query }) {
  const normalized = normalizeGeoAlias(query);
  if (!normalized) return null;

  return (
    GEO_ALIAS_PRESETS.find((preset) => {
      if (preset.country !== country) return false;
      if (preset.province !== province) return false;

      return preset.aliases.some(
        (alias) => normalizeGeoAlias(alias) === normalized
      );
    }) || null
  );
}

export default function useNatalGeo({ form, setForm }) {
  const [geoResults, setGeoResults] = useState([]);
  const [geoOpen, setGeoOpen] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);
  const [geoTimezoneResolving, setGeoTimezoneResolving] = useState(false);
  const [geoTimezoneError, setGeoTimezoneError] = useState(false);

  const cityInputRef = useRef(null);
  const actionsRef = useRef(null);
  const backBtnRef = useRef(null);
  const requestSequenceRef = useRef(0);
  const geoRequestIdRef = useRef(0);
  const selectionSequenceRef = useRef(0);
  const coordinateTimezoneCacheRef = useRef(new Map());

  const writeGeoResults = (next, writer, requestId = null) => {
    const safeResults = Array.isArray(next) ? next : [];
    debugGeoStateCommit(writer, safeResults.length, safeResults);
    if (requestId != null) debugGeoResponseCommit(requestId, writer, safeResults.length, safeResults);
    setGeoResults(safeResults);
  };

  const refreshDropdownRect = () => {
    const el = cityInputRef.current;
    if (!el) return;

    const inputRect = el.getBoundingClientRect();
    const margin = 14;

    const actionsTop = actionsRef.current?.getBoundingClientRect?.().top;
    const backTop = backBtnRef.current?.getBoundingClientRect?.().top;

    const nearestBottomLimit = [actionsTop, backTop]
      .filter((value) => Number.isFinite(value))
      .reduce(
        (min, value) => Math.min(min, value),
        Number.POSITIVE_INFINITY
      );

    const rawSpaceBelow =
      window.innerHeight - inputRect.bottom - margin;

    const reservedSpaceBelow = Number.isFinite(nearestBottomLimit)
      ? nearestBottomLimit - inputRect.bottom - 10 - margin
      : rawSpaceBelow;

    const spaceBelow = Math.max(
      0,
      Math.min(rawSpaceBelow, reservedSpaceBelow)
    );

    const spaceAbove = Math.max(0, inputRect.top - margin);
    const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;

    const maxHeight = Math.max(
      140,
      Math.min(280, openUp ? spaceAbove : spaceBelow || 220)
    );

    setDropdownRect({
      left: inputRect.left,
      width: inputRect.width,
      top: inputRect.top,
      bottom: inputRect.bottom,
      openUp,
      maxHeight,
    });
  };

  useEffect(() => {
    if (!geoOpen) return;

    refreshDropdownRect();
    const onResizeOrScroll = () => refreshDropdownRect();

    window.addEventListener('resize', onResizeOrScroll);
    window.addEventListener('scroll', onResizeOrScroll, true);

    return () => {
      window.removeEventListener('resize', onResizeOrScroll);
      window.removeEventListener('scroll', onResizeOrScroll, true);
    };
  }, [geoOpen]);

  const buildBirthPlaceString = (nextForm) => {
    const country = nextForm.birthCountry;
    const provKey = nextForm.birthProvince;

    if (provKey === 'OTRO' || nextForm.birthCity === 'OTRO') {
      return (nextForm.birthPlaceManual || '').trim();
    }

    if (provKey === 'CABA') {
      const query = (nextForm.birthCityQuery || '').trim();

      if (query) {
        const picked = (nextForm.birthPlace || '').trim();

        if (
          picked &&
          nextForm.birthLat != null &&
          nextForm.birthLon != null
        ) {
          return picked;
        }

        const provinceLabel =
          GEO[country]?.provinces?.[provKey]?.label || provKey;

        return `${query}, ${provinceLabel}, ${
          GEO[country]?.label || country
        }`;
      }

      const city =
        GEO[country]?.provinces?.[provKey]?.cities?.[
          nextForm.birthCity
        ];

      if (city) {
        return `${city.label}, ${GEO[country]?.label || country}`;
      }

      return (nextForm.birthPlace || '').trim() || '';
    }

    const picked = (nextForm.birthPlace || '').trim();
    if (picked) return picked;

    const query = (nextForm.birthCityQuery || '').trim();
    if (!query) return '';

    const provinceLabel =
      provKey === '__ANY__'
        ? ''
        : GEO[country]?.provinces?.[provKey]?.label || provKey;

    return provinceLabel
      ? `${query}, ${provinceLabel}, ${
          GEO[country]?.label || country
        }`
      : `${query}, ${GEO[country]?.label || country}`;
  };

  const resolveCoords = (nextForm) => {
    const country = nextForm.birthCountry;
    const provKey = nextForm.birthProvince;

    if (provKey === 'OTRO' || nextForm.birthCity === 'OTRO') {
      return { lat: null, lon: null };
    }

    if (provKey === 'CABA') {
      const query = (nextForm.birthCityQuery || '').trim();

      if (!query) {
        const city =
          GEO[country]?.provinces?.[provKey]?.cities?.[
            nextForm.birthCity
          ];

        if (city?.lat != null && city?.lon != null) {
          return { lat: city.lat, lon: city.lon };
        }
      }
    }

    if (
      nextForm.birthLat != null &&
      nextForm.birthLon != null
    ) {
      return {
        lat: nextForm.birthLat,
        lon: nextForm.birthLon,
      };
    }

    return { lat: null, lon: null };
  };

  const fetchGeoSuggestions = async (query) => {
    const requestSequence = ++requestSequenceRef.current;
    const requestId = ++geoRequestIdRef.current;
    const normalized = String(query || '').trim();

    if (normalized.length < 3) {
      writeGeoResults([], 'fetchGeoSuggestions');
      return [];
    }

    setGeoLoading(true);

    try {
      const params = toSearchParams({
          q: normalized,
          country: form.birthCountry,
          province: geoRequestProvince(form.birthProvince),
          birthDate: form.birthDate,
          birthTime: form.birthTime,
      });
      const data = await authenticatedRequest(`/api/natal/geocode?${params.toString()}`, {
        credentials: 'include',
        diagSource: 'useNatalGeo.search',
      });
      debugGeoRequest(
        requestId,
        'fetchGeoSuggestions',
        normalized,
        form.birthCountry,
        form.birthProvince,
        Array.isArray(data?.results) ? data.results.length : 0
      );

      const eligibleResults = Array.isArray(data?.results)
        ? filterGeoCandidatesForContext(data.results, { country: form.birthCountry })
        : [];
      const results = Array.isArray(data?.results)
        ? finalizeGeoResults(
          rankGeoCandidates(eligibleResults, form.birthCityQuery || normalized, {
            country: form.birthCountry,
            province: form.birthProvince,
            city: form.birthCity,
          }),
          form.birthCityQuery || normalized,
          {
            country: form.birthCountry,
            province: form.birthProvince,
            city: form.birthCity,
          }
        ).slice(0, 6)
        : [];

      if (requestSequence === requestSequenceRef.current) writeGeoResults(results, 'fetchGeoSuggestions', requestId);
      return results;
    } catch (error) {
      if (requestSequence === requestSequenceRef.current) writeGeoResults([], 'fetchGeoSuggestions');
      return [];
    } finally {
      if (requestSequence === requestSequenceRef.current) setGeoLoading(false);
    }
  };

  const resolveCoordinateTimezone = async (pick) => {
    const lat = Number(pick?.lat);
    const lon = Number(pick?.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

    const cacheKey = [
      lat.toFixed(6),
      lon.toFixed(6),
      form.birthDate || '',
      form.birthTime || '',
    ].join('|');
    if (coordinateTimezoneCacheRef.current.has(cacheKey)) {
      return coordinateTimezoneCacheRef.current.get(cacheKey);
    }

    const params = toSearchParams({
      lat,
      lon,
      birthDate: form.birthDate,
      birthTime: form.birthTime,
    });

    const data = await authenticatedRequest(`/api/natal/timezone?${params.toString()}`, {
      credentials: 'include',
      diagSource: 'useNatalGeo.coordinateTimezone',
    });

    if (!data?.timezone) return null;
    const resolved = {
      timezone: data.timezone,
      tzOffsetMin: typeof data.tzOffsetMin === 'number' ? data.tzOffsetMin : null,
    };
    coordinateTimezoneCacheRef.current.set(cacheKey, resolved);
    return resolved;
  };

  const invalidateGeoTimezoneResolution = () => {
    selectionSequenceRef.current += 1;
    setGeoTimezoneResolving(false);
    setGeoTimezoneError(false);
  };

  const applyGeoPick = (pick) => {
    if (!pick) return;

    const selectionSequence = ++selectionSequenceRef.current;
    const hasCoordinates = Number.isFinite(Number(pick.lat)) && Number.isFinite(Number(pick.lon));
    const hasTimezone = typeof pick.timezone === 'string' && pick.timezone.trim();
    setGeoTimezoneError(false);
    setGeoTimezoneResolving(hasCoordinates && !hasTimezone);

    setForm((prev) => {
      const canonical = canonicalGeoFields(pick, prev);

      return {
        ...prev,
        ...canonical,
        birthCityQuery:
          pick.name ||
          pick.primaryName ||
          pick.display ||
          pick.complete_name ||
          '',
      };
    });

    writeGeoResults([], 'applyGeoPick');
    setGeoOpen(false);

    if (hasCoordinates && !hasTimezone) {
      resolveCoordinateTimezone(pick)
        .then((resolvedTimezone) => {
          if (!resolvedTimezone || selectionSequence !== selectionSequenceRef.current) {
            if (selectionSequence === selectionSequenceRef.current) setGeoTimezoneError(true);
            return;
          }

          setForm((prev) => ({
            ...prev,
            birthTimezone: resolvedTimezone.timezone,
            birthTzOffsetMin: resolvedTimezone.tzOffsetMin,
          }));
        })
        .catch(() => {
          if (selectionSequence === selectionSequenceRef.current) setGeoTimezoneError(true);
        })
        .finally(() => {
          if (selectionSequence === selectionSequenceRef.current) setGeoTimezoneResolving(false);
        });
    }
  };

  const resolveTypedPlaceData = async (rawValue) => {
    const typed = String(rawValue || '').trim();
    if (typed.length < 3) return null;

    const preset = findGeoAliasPreset({
      country: form.birthCountry,
      province: form.birthProvince,
      query: typed,
    });

    if (preset) {
      return {
        birthCityQuery: typed,
        birthPlace: preset.place,
        birthLat: preset.lat,
        birthLon: preset.lon,
        birthTzOffsetMin: preset.tzOffsetMin,
        birthTimezone: preset.timezone,
        geoResults: [],
      };
    }

    const province = form.birthProvince;

    const escapedProvince = String(province || '').replace(
      /[.*+?^${}()|[\]\\]/g,
      '\\$&'
    );

    const shouldPrefixProvince =
      province &&
      province !== 'CABA' &&
      province !== '__ANY__' &&
      !new RegExp(escapedProvince, 'i').test(typed);

    const query = shouldPrefixProvince
      ? `${typed}, ${province}`
      : typed;

    const requestSequence = ++requestSequenceRef.current;
    const requestId = ++geoRequestIdRef.current;
    setGeoLoading(true);

    try {
      const params = toSearchParams({
          q: query,
          country: form.birthCountry,
          province: geoRequestProvince(province),
          birthDate: form.birthDate,
          birthTime: form.birthTime,
      });
      const data = await authenticatedRequest(`/api/natal/geocode?${params.toString()}`, {
        credentials: 'include',
        diagSource: 'useNatalGeo.resolve',
      });
      debugGeoRequest(
        requestId,
        'applyResolvedTypedPlace',
        query,
        form.birthCountry,
        province,
        Array.isArray(data?.results) ? data.results.length : 0
      );

      const eligibleResults = Array.isArray(data?.results)
        ? filterGeoCandidatesForContext(data.results, { country: form.birthCountry })
        : [];
      const results = Array.isArray(data?.results)
        ? finalizeGeoResults(
          rankGeoCandidates(eligibleResults, typed, {
            country: form.birthCountry,
            province: form.birthProvince,
            city: form.birthCity,
          }),
          typed,
          {
            country: form.birthCountry,
            province: form.birthProvince,
            city: form.birthCity,
          }
        )
        : [];

      const best = results[0];
      if (!best || !isLatestGeoRequest(requestSequence, requestSequenceRef.current)) return null;

      let timezone = typeof best.timezone === 'string' && best.timezone ? best.timezone : null;
      let tzOffsetMin = typeof best.tzOffsetMin === 'number' ? best.tzOffsetMin : null;
      if (!timezone && Number.isFinite(Number(best.lat)) && Number.isFinite(Number(best.lon))) {
        try {
          const resolvedTimezone = await resolveCoordinateTimezone(best);
          timezone = resolvedTimezone?.timezone || null;
          tzOffsetMin = resolvedTimezone?.tzOffsetMin ?? null;
        } catch (error) {
          timezone = null;
          tzOffsetMin = null;
        }
      }

      return {
        birthCityQuery: typed,
        birthPlace:
          best.canonicalName ||
          canonicalGeoLabel(best) ||
          best.complete_name ||
          best.display ||
          best.name ||
          buildBirthPlaceString({
            ...form,
            birthCityQuery: typed,
          }),
        birthLat: Number.isFinite(Number(best.lat))
          ? Number(best.lat)
          : null,
        birthLon: Number.isFinite(Number(best.lon))
          ? Number(best.lon)
          : null,
        birthTzOffsetMin: tzOffsetMin,
        birthTimezone: timezone,
        geoResults: results.slice(0, 6),
      };
    } catch (error) {
      return null;
    } finally {
      if (requestSequence === requestSequenceRef.current) setGeoLoading(false);
    }
  };

  const applyResolvedTypedPlace = (resolved) => {
    if (!resolved) return false;

    setForm((prev) => ({
      ...prev,
      birthCityQuery: resolved.birthCityQuery || form.birthCityQuery,
      birthPlace: resolved.birthPlace,
      birthLat: resolved.birthLat,
      birthLon: resolved.birthLon,
      birthTzOffsetMin: resolved.birthTzOffsetMin,
      birthTimezone: resolved.birthTimezone,
    }));

    // Typed/blur resolution owns canonical form fields, not suggestion state.
    // Keep the public suggestion list owned by the debounced search writer.
    writeGeoResults([], 'applyResolvedTypedPlace');

    setGeoOpen(false);
    return true;
  };

  const tryResolveTypedPlace = async (rawValue) => {
    const resolved = await resolveTypedPlaceData(rawValue);

    if (!resolved) return null;

    applyResolvedTypedPlace(resolved);
    return resolved;
  };

  const updateGeo = (patch) => {
    invalidateGeoTimezoneResolution();
    setForm((prev) => {
      const next = { ...prev, ...patch };

      if (patch.birthCountry || patch.birthProvince) {
        if (patch.birthCountry) {
          next.birthProvince =
            Object.keys(
              GEO[next.birthCountry]?.provinces || {}
            )[0] || 'OTRO';
        }

        const province =
          GEO[next.birthCountry]?.provinces?.[
            next.birthProvince
          ];

        const cityKeys = Object.keys(
          province?.cities || {}
        );

        next.birthCity =
          next.birthProvince === 'CABA'
            ? cityKeys[0] || 'OTRO'
            : cityKeys.length
              ? cityKeys[0]
              : '__SEARCH__';

        next.birthNeighborhood = '';
        next.birthCityQuery = '';
        next.birthPlaceManual = '';
        next.birthLat = null;
        next.birthLon = null;
        next.birthTzOffsetMin = null;
        next.birthTimezone = null;
      }

      if (patch.birthCity) {
        next.birthNeighborhood = '';

        if (next.birthProvince !== 'CABA') {
          next.birthCityQuery = '';
          next.birthLat = null;
          next.birthLon = null;
          next.birthTzOffsetMin = null;
          next.birthTimezone = null;
        }
      }

      const birthPlace = buildBirthPlaceString(next);
      const { lat, lon } = resolveCoords(next);

      return {
        ...next,
        birthPlace,
        birthLat: lat,
        birthLon: lon,
      };
    });
  };

  useEffect(() => {
    setForm((prev) => {
      if (prev.birthPlace) return prev;

      const birthPlace = buildBirthPlaceString(prev);
      const { lat, lon } = resolveCoords(prev);

      return {
        ...prev,
        birthPlace,
        birthLat: lat,
        birthLon: lon,
      };
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (form.birthProvince === 'OTRO') return;

    const query = String(
      form.birthCityQuery || ''
    ).trim();

    if (query.length < 3) {
      writeGeoResults([], 'queryClear');
      return;
    }

      const timeoutId = setTimeout(() => {
      fetchGeoSuggestions(query);
      }, 450);

    return () => clearTimeout(timeoutId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.birthCityQuery,
    form.birthProvince,
    form.birthCountry,
  ]);

  debugGeoTrace('[NATAL_GEO_HOOK_TRACE]', geoResults);

  return {
    actionsRef,
    applyGeoPick,
    applyResolvedTypedPlace,
    backBtnRef,
    buildBirthPlaceString,
    cityInputRef,
    dropdownRect,
    findGeoAliasPreset,
    geoLoading,
    geoTimezoneError,
    geoTimezoneResolving,
    invalidateGeoTimezoneResolution,
    geoOpen,
    geoResults,
    refreshDropdownRect,
    resolveCoords,
    resolveTypedPlaceData,
    setGeoOpen,
    setGeoResults: (next) => writeGeoResults(
      finalizeGeoResults(Array.isArray(next) ? next : [], form.birthCityQuery, {
        country: form.birthCountry,
        province: form.birthProvince,
        city: form.birthCity,
      }),
      'externalSafeSetter'
    ),
    tryResolveTypedPlace,
    updateGeo,
  };
}
